<script setup lang="ts">
import { computed } from "vue";
import type { ItemDTO } from "@barkup/shared";
import { formatBytes, formatShortDate, senderLabel } from "../format.js";

/**
 * Bottom sheet behind each row's ⋮ — the file-manager pattern that replaced
 * the old per-row button strip. One sheet instance per view; `item` null
 * means closed. Flat + tonal per the design system: the panel is a surface
 * step + 1px border, elevation is the dimmed backdrop, never a box-shadow.
 */
const props = withDefaults(
  defineProps<{
    item: ItemDTO | null;
    trash: boolean;
    /** Off in the account-deletion flow: these are the caller's own files, and
     * reporting them would route out of a flow they're partway through. */
    canReport?: boolean;
  }>(),
  { canReport: true }
);

const emit = defineEmits<{
  close: [];
  open: [id: string];
  download: [item: ItemDTO];
  select: [id: string];
  remove: [id: string];
  restore: [id: string];
  report: [item: ItemDTO];
}>();

const title = computed(() => props.item?.filename ?? props.item?.ogTitle ?? props.item?.linkUrl ?? "(ไม่มีชื่อ)");
const metaLine = computed(() => {
  if (!props.item) return "";
  const parts = [senderLabel(props.item), formatShortDate(props.item.createdAt)];
  if (Number(props.item.sizeBytes)) parts.push(formatBytes(props.item.sizeBytes));
  return parts.join(" • ");
});
const downloadable = computed(() => props.item !== null && props.item.kind !== "LINK" && !props.trash);

function act(fn: () => void): void {
  fn();
  emit("close");
}
</script>

<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div v-if="item" class="fixed inset-0 z-[70] flex flex-col justify-end" @click.self="emit('close')">
        <div class="sheet-backdrop absolute inset-0 bg-on-surface/40" aria-hidden="true" @click="emit('close')" />
        <div
          class="sheet-panel safe-pb relative rounded-t-2xl border-t border-outline-variant bg-surface"
          role="menu"
          :aria-label="title"
        >
          <div class="mx-auto mt-2 h-1 w-9 rounded-full bg-outline-variant" aria-hidden="true" />

          <div class="border-b border-outline-variant px-5 py-3">
            <p class="truncate font-body-md font-medium text-on-surface">{{ title }}</p>
            <p class="truncate font-metadata text-on-surface-variant">{{ metaLine }}</p>
          </div>

          <div class="flex flex-col py-1">
            <button type="button" role="menuitem" class="sheet-action" @click="act(() => emit('open', item!.id))">
              <span class="material-symbols-outlined text-[20px]">visibility</span> เปิดดู
            </button>
            <button
              v-if="downloadable"
              type="button"
              role="menuitem"
              class="sheet-action"
              @click="act(() => emit('download', item!))"
            >
              <span class="material-symbols-outlined text-[20px]">download</span> ดาวน์โหลด
            </button>
            <button type="button" role="menuitem" class="sheet-action" @click="act(() => emit('select', item!.id))">
              <span class="material-symbols-outlined text-[20px]">check_circle</span> เลือกหลายรายการ
            </button>
            <button
              v-if="trash"
              type="button"
              role="menuitem"
              class="sheet-action"
              @click="act(() => emit('restore', item!.id))"
            >
              <span class="material-symbols-outlined text-[20px]">restore</span> กู้คืน
            </button>
            <button
              v-else
              type="button"
              role="menuitem"
              class="sheet-action text-error"
              @click="act(() => emit('remove', item!.id))"
            >
              <span class="material-symbols-outlined text-[20px]">delete</span> ย้ายไปถังขยะ
            </button>
            <!-- Separated from the actions above: reporting is about someone
                 else's content, not about managing your own file. -->
            <button
              v-if="canReport"
              type="button"
              role="menuitem"
              class="sheet-action border-t border-outline-variant"
              @click="act(() => emit('report', item!))"
            >
              <span class="material-symbols-outlined text-[20px]">flag</span> รายงานเนื้อหานี้
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sheet-action {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 52px;
  padding: 0 20px;
  font-size: 16px;
  line-height: 24px;
  color: var(--sheet-action-color, theme("colors.on-surface"));
  text-align: left;
  transition: background-color 150ms;
}
.sheet-action:active {
  background-color: theme("colors.surface-container");
}
.sheet-action.text-error {
  color: theme("colors.error");
}

/* Motion: 200ms slide-up + backdrop fade; reduced motion = plain crossfade. */
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 200ms cubic-bezier(0.25, 1, 0.5, 1);
}
.sheet-enter-active .sheet-panel,
.sheet-leave-active .sheet-panel {
  transition: transform 200ms cubic-bezier(0.25, 1, 0.5, 1);
}
.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}
.sheet-enter-from .sheet-panel,
.sheet-leave-to .sheet-panel {
  transform: translateY(100%);
}
@media (prefers-reduced-motion: reduce) {
  .sheet-enter-active .sheet-panel,
  .sheet-leave-active .sheet-panel {
    transition: none;
  }
  .sheet-enter-from .sheet-panel,
  .sheet-leave-to .sheet-panel {
    transform: none;
  }
}
</style>
